import * as React from "react";
import {connect} from "react-redux";
import {Dispatch} from "redux";
import {User, UserManager} from "oidc-client";
import AppSettings from "../types/AppSettings";
import ErrorPage from "./ErrorPage";
import NavMenu from "./NavMenu";
import {Container} from "react-bootstrap";
import {Redirect, Switch} from "react-router";
import {Route} from "react-router-dom";
import PageNotFound from "./PageNotFound";
import Footer from "./Footer";
import AppRoute from "../types/AppRoute";
import {userHasAllRoles, userHasAnyRole} from "../utils/userRoleHelpers";
import {CommonAppState} from "../store";
import {RouterLocation} from "connected-react-router";
import {History, LocationState} from "history";
import ErrorHandler from "../features/errors/ErrorHandler";
import {getFoundationServiceSchemeAndHost} from "../utils/getFoundationServiceSchemeAndHost";

type SecuredApplicationProps = {
  appSettings: AppSettings;
  userManager: UserManager;
  history: History<LocationState>;
  user: User;
  isLoadingUser: boolean;
  dispatch: Dispatch;
  location: RouterLocation<LocationState>;
};

interface SecuredApplicationState {
  isAuthorized: boolean;
  callbackError: Error | undefined;
}

class SecuredApplication extends React.Component<SecuredApplicationProps, SecuredApplicationState> {
  public constructor(props: SecuredApplicationProps) {
    super(props);

    this.state = {
      isAuthorized: false,
      callbackError: undefined,
    };
  }

  private hasHandledCallback: boolean = false;
  private hasRedirectedExternally: boolean = false;

  public componentDidMount() {
    this.evaluateAuthorization();
  }

  public componentDidUpdate() {
    this.evaluateAuthorization();
  }

  private evaluateAuthorization() {
    console.debug("[SecuredApplication] evaluate-authorization");

    // Check to see if this is a signin callback
    if (this.props.location.pathname.substring(0, 9) === "/callback") {
      // Make sure we haven't already handled the callback
      if (this.hasHandledCallback) {
        return;
      }

      // Handle the signin callback
      console.debug("[SecuredApplication] handle-callback");
      this.hasHandledCallback = true;
      this.props.userManager
        .signinRedirectCallback()
        .then((user) => this.callbackSuccess(user))
        .catch((error) => this.callbackError(error));
      return;
    }

    // Make sure we haven't already initiated a signin or no-roles redirect
    if (this.hasRedirectedExternally) {
      return;
    }

    // Make sure the user isn't still loading
    if (this.props.isLoadingUser) {
      return;
    }

    // Check to see if the user isn't logged in
    if (!this.props.user) {
      console.debug("[SecuredApplication] no-user");
      this.hasRedirectedExternally = true;
      this.setState({isAuthorized: false});

      // Signin redirect
      console.debug("[SecuredApplication] signin-redirect");
      this.props.userManager.signinRedirect({state: window.location.href});

      return;
    }

    // Check to see if the user's login has expired
    if (this.props.user.expired) {
      console.debug("[SecuredApplication] expired-user");
      this.hasRedirectedExternally = true;
      this.setState({isAuthorized: false});

      // Remove any existing session
      console.debug("[SecuredApplication] remove-expired-user");
      this.props.userManager.removeUser();

      // Signin redirect
      console.debug("signin-redirect");
      this.props.userManager.signinRedirect({state: window.location.href});

      return;
    }

    // Check to see if the user hadn't been fully authorized, but is now
    if (
      !this.state.isAuthorized &&
      !this.props.isLoadingUser &&
      this.props.user &&
      !this.props.user.expired &&
      (!this.props.appSettings.requireValidRole || userHasAnyRole(this.props.user, this.props.appSettings.appRoles))
    ) {
      console.debug("[SecuredApplication] user-authorized");

      // Render routes
      this.setState({isAuthorized: true});
    }
  }

  private callbackSuccess(user: User) {
    console.debug("[SecuredApplication] callback-success");

    // Check to see if the user lacks required roles
    if (!user || (this.props.appSettings.requireValidRole && !userHasAnyRole(user, this.props.appSettings.appRoles))) {
      console.debug("[SecuredApplication] callback-no-roles");
      this.hasRedirectedExternally = true;

      // Remove any existing session
      console.debug("[SecuredApplication] callback-no-roles-remove-user");
      this.props.userManager.removeUser();

      // Redirect back to Foundation Service
      console.debug("[SecuredApplication] callback-no-roles-redirect");
      const redirectUri = `${getFoundationServiceSchemeAndHost()}/applications/${
        this.props.appSettings.clientId
      }/no-roles`;
      window.location.replace(redirectUri);
      return;
    }

    // get the user's previous location, passed during signinRedirect()
    var redirectUrl = "/";
    if (typeof user.state === "string") {
      const a = document.createElement("a");
      a.href = user.state;
      redirectUrl = a.pathname;
      if (redirectUrl.startsWith(`/${this.props.appSettings.basePath}`)) {
        redirectUrl = redirectUrl.substring(this.props.appSettings.basePath.length + 1);
      }
    }

    // Redirect to previous page
    console.debug("[SecuredApplication] callback-authorized-redirect", redirectUrl);
    this.props.history.push(redirectUrl);
  }

  private callbackError(error: Error) {
    console.debug("[SecuredApplication] callback-error");
    this.setState({callbackError: error});
  }

  public render() {
    // Display an error if one has occurred
    if (this.state.callbackError !== undefined) {
      return <ErrorPage settings={this.props.appSettings} error={this.state.callbackError as Error} />;
    }

    // Wait for the user to be fully signed in and authorized
    if (!this.state.isAuthorized) {
      // TODO: Make this a pretty spinner or some shit?
      return <div>Loading...</div>;
    } else {
      return (
        <React.Fragment>
          <NavMenu appSettings={this.props.appSettings} userManager={this.props.userManager} />
          <Container fluid className="content">
            <ErrorHandler />
            <Switch>
              {this.props.appSettings.routes.map((route: AppRoute, routeIndex: number) => {
                // Bail if the user doesn't have required roles
                if (
                  (route.requireAllRoles !== undefined && !userHasAllRoles(this.props.user, route.requireAllRoles)) ||
                  (route.requireAnyRoles !== undefined && !userHasAnyRole(this.props.user, route.requireAnyRoles))
                ) {
                  return (
                    <Route exact key={routeIndex} path={route.path}>
                      <Redirect to="/not-found" />
                    </Route>
                  );
                }

                return <Route exact key={routeIndex} path={route.path} component={route.component} />;
              })}
              <Route path="/not-found">
                <PageNotFound />
              </Route>
              <Redirect to="/not-found" />
            </Switch>
          </Container>
          <Footer />
        </React.Fragment>
      );
    }
  }
}

function mapStateToProps(state: CommonAppState) {
  return {
    user: state.oidc.user,
    isLoadingUser: state.oidc.isLoadingUser,
    location: state.router.location,
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    dispatch,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(SecuredApplication);
